import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function GreenPointPage() {
  useEffect(() => {
    // Guard against SSR where `document` may be undefined
    if (typeof document === 'undefined') return;

    try {
      document.title =
        'Green Point Painters Cape Town | Luxury Painting Services | Waterfront Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Premium painting services in Green Point, Cape Town. Specialist luxury residential and commercial painting for waterfront properties. Expert marine-grade coatings and finishes.'
        );
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Green Point painters, luxury painting Cape Town, waterfront property painting'
        );
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/green-point`
        );
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'Green Point Painters Cape Town',
        description: 'Premium painting services in Green Point, Cape Town',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/green-point`,
        areaServed: {
          '@type': 'Place',
          name: 'Green Point, Cape Town, Western Cape, South Africa',
        },
        serviceArea: {
          '@type': 'GeoCircle',
          geoMidpoint: {
            '@type': 'GeoCoordinates',
            latitude: -33.9057,
            longitude: 18.4068,
          },
          geoRadius: '10000',
        },
      });
      document.head.appendChild(script);
    } catch (err) {
      // Log the error but avoid breaking the UI
      console.error('Error setting up meta tags or JSON‑LD script:', err);
    }

    return () => {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript && existingScript.textContent?.includes('Green Point Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-building-2-line',
      title: 'Luxury Residential Painting',
      description:
        'Premium painting services for high‑end apartments, penthouses, and waterfront properties with marine‑grade protection. Our specialized approach ensures your luxury residence maintains its pristine appearance despite the challenging coastal environment. We use only the finest quality paints and materials specifically formulated to withstand salt air, humidity, and UV exposure.',
      features: [
        'Marine‑grade coatings',
        'Premium finishes',
        'Color consultation',
        'Minimal disruption',
        'Luxury materials',
        'Detailed preparation'
      ],
    },
    {
      icon: 'ri-store-2-line',
      title: 'Commercial Waterfront Painting',
      description:
        'Professional painting for restaurants, hotels, and retail spaces in the prestigious Green Point commercial district. We understand the unique demands of hospitality and retail environments, offering flexible scheduling and efficient execution to minimize business disruption while delivering exceptional results that enhance your brand image.',
      features: [
        'Restaurant painting',
        'Hotel maintenance',
        'Retail spaces',
        'After‑hours service',
        'Brand color matching',
        'Quick turnaround'
      ],
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Coastal Protection Systems',
      description:
        'Specialized protective coatings designed to withstand salt air, UV exposure, and coastal weather conditions. Our advanced coating systems provide superior protection against the harsh marine environment, ensuring your property maintains its beauty and structural integrity for years to come with minimal maintenance requirements.',
      features: [
        'Salt air protection',
        'UV resistance',
        'Weather sealing',
        'Long‑term durability',
        'Anti‑corrosion',
        'Moisture barriers'
      ],
    },
    {
      icon: 'ri-home-smile-line',
      title: 'Penthouse & High-Rise Painting',
      description:
        'Specialized services for luxury penthouses and high-rise apartments with panoramic ocean views. We employ advanced access techniques and safety protocols to deliver flawless results at any height, ensuring your prestigious property receives the premium treatment it deserves with minimal inconvenience to residents.',
      features: [
        'High-rise access',
        'Panoramic views protection',
        'Luxury finishes',
        'Resident coordination',
        'Safety certified',
        'Premium scheduling'
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Exterior Waterfront Painting',
      description:
        'Comprehensive exterior painting services specifically designed for waterfront properties facing the Atlantic Ocean. Our marine-grade systems protect against salt spray, wind-driven rain, and intense UV radiation while maintaining stunning aesthetics that complement Green Point\'s sophisticated architectural landscape.',
      features: [
        'Marine-grade systems',
        'Salt spray protection',
        'Wind resistance',
        'UV protection',
        'Color retention',
        'Weather warranty'
      ],
    },
    {
      icon: 'ri-tools-line',
      title: 'Waterproofing & Restoration',
      description:
        'Advanced waterproofing solutions and restoration services for coastal properties experiencing moisture issues or weather damage. Our comprehensive approach addresses both aesthetic and structural concerns, ensuring your Green Point property remains protected against the elements while looking its absolute best.',
      features: [
        'Moisture remediation',
        'Structural protection',
        'Crack repair',
        'Seal restoration',
        'Preventive maintenance',
        'Long-term warranty'
      ],
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Luxury Property Assessment',
      description:
        'Comprehensive evaluation of your Green Point property, including detailed inspection of coastal exposure, existing coating condition, and specific environmental challenges. We assess salt air impact, UV damage, and moisture issues to develop a customized protection strategy.',
    },
    {
      step: 2,
      title: 'Premium Material Selection',
      description:
        'Expert consultation on the finest marine-grade paints and coatings specifically formulated for waterfront properties. We recommend premium products that offer superior protection against coastal conditions while achieving your desired aesthetic vision.',
    },
    {
      step: 3,
      title: 'Meticulous Surface Preparation',
      description:
        'Thorough preparation including power washing, salt deposit removal, surface repairs, and primer application. Our detailed prep work ensures optimal adhesion and longevity of the coating system, addressing any underlying issues before painting begins.',
    },
    {
      step: 4,
      title: 'Expert Application',
      description:
        'Professional application by experienced craftsmen using advanced techniques and equipment. We employ multiple coat systems with proper curing times between applications to ensure maximum durability and a flawless finish.',
    },
    {
      step: 5,
      title: 'Quality Inspection',
      description:
        'Rigorous quality control inspection of all painted surfaces, checking for uniform coverage, proper adhesion, and flawless finish. We ensure every detail meets our exacting standards and your expectations before project completion.',
    },
    {
      step: 6,
      title: 'Maintenance Guidance',
      description:
        'Comprehensive maintenance recommendations and care instructions to maximize the lifespan of your new coating system. We provide detailed guidance on cleaning, inspection schedules, and preventive maintenance specific to coastal properties.',
    },
  ];

  const recentProjects = [
    {
      title: 'Waterfront Penthouse Transformation',
      location: 'Green Point Waterfront',
      description:
        'Complete luxury renovation of a 3‑bedroom penthouse with panoramic harbor views. This prestigious project included interior and exterior painting with premium marine-grade coatings, custom color consultation, and specialized finishes throughout.',
      image:
        'https://public.readdy.ai/ai/img_res/3dc4e6621f8f07a3939ecdfb6f161ebb.jpg',
    },
    {
      title: 'Boutique Hotel Complete Renovation',
      location: 'Green Point Commercial District',
      description:
        'Comprehensive painting renovation for a 4‑star boutique hotel including 24 guest rooms, lobby, restaurant, and exterior facades. Completed with minimal guest disruption using after-hours scheduling and efficient project management.',
      image:
        'https://readdy.ai/api/search-image?query=Boutique%20hotel%20painting%20renovation%20in%20Green%20Point%2C%20luxury%20hospitality%20property%20painting%2C%20elegant%20hotel%20interior%20and%20exterior%2C%20professional%20commercial%20painting%20Cape%20Town&width=400&height=300&seq=green-point-project-2&orientation=landscape',
    },
    {
      title: 'Luxury Apartment Complex Restoration',
      location: 'Green Point Central',
      description:
        'Exterior painting and waterproofing of a prestigious 12‑story residential complex housing 48 luxury apartments. Project included complete facade restoration, balcony waterproofing, and marine-grade protective coatings.',
      image:
        'https://readdy.ai/api/search-image?query=Luxury%20apartment%20complex%20exterior%20painting%20in%20Green%20Point%20Cape%20Town%2C%20high-rise%20residential%20building%20with%20marine-grade%20coatings%2C%20modern%20waterfront%20architecture%2C%20professional%20building%20painting&width=400&height=300&seq=green-point-project-3&orientation=landscape',
    },
    {
      title: 'Waterfront Restaurant Makeover',
      location: 'Green Point Promenade',
      description:
        'Complete interior and exterior transformation of a popular waterfront restaurant. Included custom color scheme development, specialized kitchen-grade coatings, and weather-resistant exterior finishes to withstand ocean spray.',
      image:
        'https://readdy.ai/api/search-image?query=Waterfront%20restaurant%20painting%20in%20Green%20Point%20Cape%20Town%2C%20luxury%20dining%20establishment%20with%20ocean%20views%2C%20professional%20commercial%20painting%2C%20modern%20coastal%20restaurant%20design&width=400&height=300&seq=green-point-project-4&orientation=landscape',
    },
  ];

  const testimonials = [
    {
      name: 'Alexandra Winters',
      location: 'Green Point Waterfront Penthouse',
      rating: 5,
      text:
        'Exceptional service for our penthouse renovation. The marine-grade coatings have held up beautifully against the coastal conditions for over two years now. The team was professional, punctual, and respectful of our luxury home. Truly premium quality work that exceeded our expectations.',
    },
    {
      name: 'James Morrison',
      location: 'Green Point Boutique Hotel',
      rating: 5,
      text:
        'Outstanding commercial painting service for our hotel. They worked around our guests with minimal disruption, completing the project ahead of schedule. The quality of workmanship is exceptional and our guests have commented on how fresh and elegant everything looks. Highly professional team.',
    },
    {
      name: 'Catherine van der Merwe',
      location: 'Green Point Luxury Apartment',
      rating: 5,
      text:
        'The attention to detail was remarkable. Our luxury apartment looks absolutely stunning and the protective coatings give us peace of mind for years to come. The color consultation was invaluable and the final result is exactly what we envisioned. Worth every cent.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Luxury waterfront apartments in Green Point Cape Town being painted, premium residential buildings near V&A Waterfront, professional painters working on high-end properties, modern coastal architecture, marine-grade painting services&width=1920&height=600&seq=green-point-hero-main&orientation=landscape"
            alt="Premium painting services in Green Point, Cape Town"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Premium <span className="text-yellow-400">Green Point Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Luxury painting services for Green Point&apos;s prestigious waterfront properties.
              Specialized marine‑grade coatings and premium finishes for discerning clients in Cape Town&apos;s most sought-after coastal neighborhood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Expert Painting Services for Green Point&apos;s Waterfront Properties
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Green Point stands as one of Cape Town&apos;s most prestigious and desirable neighborhoods, renowned for its stunning waterfront location, proximity to the V&A Waterfront, and collection of luxury residential and commercial properties. This vibrant coastal suburb demands painting services that match its sophisticated character and can withstand the unique challenges of the marine environment.
              </p>
              <p>
                Our specialized painting services for Green Point properties combine premium materials, expert craftsmanship, and deep understanding of coastal environmental factors. The Atlantic Ocean&apos;s salt-laden air, intense UV radiation, and moisture-rich conditions require advanced coating systems and meticulous application techniques that go far beyond standard painting practices.
              </p>
              <p>
                Whether you own a luxury penthouse with panoramic ocean views, manage a boutique hotel in the commercial district, or operate a waterfront restaurant along the promenade, our team delivers painting solutions that protect your investment while enhancing its aesthetic appeal. We understand that Green Point properties represent significant investments, and our services are designed to preserve and enhance their value through superior protection and stunning visual results.
              </p>
              <p>
                The unique microclimate of Green Point, with its proximity to the ocean and exposure to prevailing winds, creates specific challenges for building exteriors and interiors. Salt spray can travel considerable distances inland, affecting properties several blocks from the waterfront. Our marine-grade coating systems provide comprehensive protection against these environmental factors while maintaining the sophisticated appearance expected in this exclusive neighborhood.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Comprehensive Luxury Painting Services in Green Point
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions for Green Point&apos;s exclusive waterfront
              properties, luxury apartments, and commercial spaces near the V&A Waterfront. Our services are tailored to meet the exacting standards of this prestigious coastal neighborhood.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow"
              >
                <div
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-6"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-black mb-6">
              Understanding Green Point&apos;s Unique Coastal Painting Challenges
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Green Point&apos;s waterfront location presents specific environmental challenges that require specialized painting approaches. The neighborhood&apos;s proximity to the Atlantic Ocean means properties are constantly exposed to salt-laden air, which can cause premature paint failure, corrosion, and surface degradation if not properly addressed with marine-grade protection systems.
              </p>
              <p>
                The prevailing south-easterly winds during summer months carry salt spray and moisture considerable distances inland, affecting not just beachfront properties but also buildings several blocks away from the ocean. This salt accumulation on painted surfaces accelerates deterioration and can lead to blistering, peeling, and discoloration if standard painting products and techniques are used.
              </p>
              <p>
                UV radiation intensity in Green Point is particularly high due to the reflective properties of the ocean and the lack of natural shade in many waterfront areas. This intense sun exposure causes rapid fading and chalking of inferior paint products, making the selection of UV-resistant coatings absolutely essential for maintaining long-term aesthetic appeal and protection.
              </p>
              <p>
                Moisture management is another critical consideration for Green Point properties. The combination of ocean humidity, morning fog, and occasional wind-driven rain creates conditions where moisture can penetrate building envelopes if proper waterproofing and coating systems are not employed. Our comprehensive approach addresses these moisture challenges through advanced preparation techniques and multi-layer protection systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Our Premium Painting Process for Green Point Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results and long-lasting protection for your waterfront property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-black">{process.step}</span>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Green Point Properties Choose Our Premium Services
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Waterfront Expertise',
                    description:
                      'Specialized knowledge of coastal painting challenges and marine‑grade solutions for Green Point properties. Our team has extensive experience working in this unique environment and understands the specific requirements for long-lasting protection.',
                  },
                  {
                    title: 'Luxury Standards',
                    description:
                      'Premium materials and meticulous attention to detail meeting the highest expectations of discerning clients. We use only top-tier products and employ craftsmen who take pride in delivering flawless results.',
                  },
                  {
                    title: 'Minimal Disruption',
                    description:
                      'Flexible scheduling and efficient processes designed to minimize impact on luxury lifestyle and business operations. We coordinate carefully with residents and businesses to ensure convenience.',
                  },
                  {
                    title: 'Long-term Protection',
                    description:
                      'Advanced coating systems providing superior protection against salt air, UV damage, and coastal weather. Our solutions are designed for durability and longevity in harsh marine environments.',
                  },
                  {
                    title: 'Local Knowledge',
                    description:
                      'Deep understanding of Green Point&apos;s architectural styles, building codes, and body corporate requirements. We navigate the unique aspects of this neighborhood with ease and professionalism.',
                  },
                  {
                    title: 'Comprehensive Warranty',
                    description:
                      'Extensive warranty coverage on both materials and workmanship, providing peace of mind for your investment. Our guarantees reflect our confidence in the quality and durability of our work.',
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-black text-sm"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional painters working on luxury Green Point waterfront property, high-end residential building painting, premium painting equipment and materials, V&A Waterfront area Cape Town, coastal property maintenance&width=600&height=700&seq=green-point-team-work&orientation=portrait"
                alt="Premium painting services in Green Point"
                className="rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Green Point Luxury Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our premium painting projects in Green Point, demonstrating our commitment to
              luxury and excellence in this prestigious waterfront neighborhood
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {recentProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="w-full h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">What Green Point Clients Say</h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied luxury property owners and businesses in Green Point
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">
                  &quot;{testimonial.text}&quot;
                </p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Enhance Your Green Point Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for a premium consultation and detailed quote. Experience the difference of
            luxury painting services designed specifically for Green Point&apos;s exclusive waterfront properties.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
            <a
              href="tel:+27123456789"
              className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}